import { gql, useMutation, useQuery } from "@apollo/client";
import { useSnackbarApi } from "@comet/admin";
import { Button } from "@mui/material";
import { ContentWrapper } from "@src/components";
// eslint-disable-next-line @comet/no-private-sibling-import
import { StyledTypography } from "@src/components/dialogs/Dialog.sc";
import InitializeDeviceDialog from "@src/components/dialogs/initializeDialog/InitializeDeviceDialog";
import { InputField } from "@src/components/fields";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import LoadingComponent from "@src/components/LoadingComponent";
import Snackbar from "@src/components/snackbar/Snackbar";
import {
    type GQLCameraAdminQuery,
    type GQLSendCameraCodeAdminMutation,
    type GQLSendCameraCodeAdminMutationVariables,
    type GQLSimplifyCodeAdminInput,
    type GQLSimplifySMSCodesAdminQuery,
} from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { useUser } from "@vivid/react-oidc-client";
import { type FormApi } from "final-form";
import { type FC, type PropsWithChildren, useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";

import { FormButtonsWrapper } from "./SMSCodeForm.sc";

const sendCameraCodeAdmin = gql`
    mutation SendCameraCodeAdmin($input: SimplifyCodeAdminInput!) {
        sendCameraCodeAdmin(input: $input)
    }
`;

const simplifySMSCodesAdminQuery = gql`
    query SimplifySMSCodesAdmin($type: CameraType!) {
        simplifySMSCodesAdmin(type: $type) {
            quick {
                label
                value
            }
            template {
                label
                value
            }
        }
    }
`;

interface SMSCodeFormProps extends GQLCameraAdminQuery {
    resetTable: () => void;
}

const SMSCodeForm: FC<PropsWithChildren<SMSCodeFormProps>> = ({ cameraAdmin, resetTable }) => {
    const intl = useIntl();
    const [sendCameraCode] = useMutation<GQLSendCameraCodeAdminMutation, GQLSendCameraCodeAdminMutationVariables>(sendCameraCodeAdmin);
    const { data, loading } = useQuery<GQLSimplifySMSCodesAdminQuery>(simplifySMSCodesAdminQuery, { variables: { type: cameraAdmin.type } });
    const snackbarApi = useSnackbarApi();
    const user = useUser();
    const [dialogOpen, setDialogOpen] = useState(false);

    const submit = async ({ code }: GQLSimplifyCodeAdminInput, form: FormApi) => {
        try {
            await sendCameraCode({
                variables: {
                    input: {
                        cameraId: cameraAdmin.id,
                        code: code,
                        executedBy: `${user?.given_name} ${user?.family_name}`,
                    },
                },
            });
            // setTimeout is required because of https://github.com/final-form/final-form/pull/229
            setTimeout(() => {
                form.reset();
                form.resetFieldState("code");
                resetTable();
            }, 0);

            snackbarApi.showSnackbar(
                <Snackbar
                    variant="success"
                    message={intl.formatMessage({
                        id: "reviermanager.camera.smsCode.success",
                        defaultMessage: "SMS-Befehl wurde gesendet",
                    })}
                    key="reviermanager.smsCode.success"
                    handleClose={snackbarApi.hideSnackbar}
                />,
            );
        } catch {
            // TODO add error handling
            snackbarApi.showSnackbar(
                <Snackbar
                    variant="error"
                    message={intl.formatMessage({
                        id: "reviermanager.camera.smsCode.failure",
                        defaultMessage: "SMS-Befehl konnte nicht gesendet werden",
                    })}
                    key="reviermanager.smsCode.error"
                    handleClose={snackbarApi.hideSnackbar}
                />,
            );
        }
    };

    if (loading || !data) {
        return <LoadingComponent />;
    }

    const options = [...data.simplifySMSCodesAdmin.quick, ...data.simplifySMSCodesAdmin.template].map((option) => {
        return { ...option, label: `${option.label} ${option.value}` };
    });

    return (
        <>
            <InitializeDeviceDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                cameraId={cameraAdmin.id}
                title={<FormattedMessage id="reviermanager.button.reconfigureCamera" defaultMessage="Kamera neu initialisieren" />}
                dialogBody={
                    <StyledTypography>
                        <FormattedMessage
                            id="reviermanager.common.reconfigureCamera"
                            defaultMessage="Möchten Sie das Gerät {deviceId} wirklich neu initialisieren?"
                            values={{ deviceId: cameraAdmin.cameraId }}
                        />
                    </StyledTypography>
                }
            />
            <ContentWrapper title={intl.formatMessage({ id: "reviermanager.camera.smsCode", defaultMessage: "SMS-Befehl" })}>
                <Form
                    onSubmit={submit}
                    onAfterSubmit={() => {
                        // do nothing
                    }}
                    render={({ pristine, hasValidationErrors, submitting, form }) => (
                        <>
                            <StaticSelectField
                                name="code"
                                options={options}
                                // Comet v8's FinalFormSelect stores the whole option object as the field value.
                                // The SMS code input below shares this field ("code") and the mutation expects the
                                // plain command string (e.g. "#500#"), so map the selected option down to its value.
                                parse={(option?: { value: string } | string) =>
                                    option != null && typeof option === "object" ? option.value : option
                                }
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.camera.smsCode.quickCode.placeholder",
                                    defaultMessage: "Quick-Befehl auswählen",
                                })}
                                components={{ IndicatorSeparator: () => null }}
                                required={false}
                                isClearable
                            />
                            <InputField
                                name="code"
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.camera.smsCode.codeInput.placeholder",
                                    defaultMessage: "SMS-Befehl eingeben ...",
                                })}
                            />
                            <FormButtonsWrapper>
                                <Button color="primary" variant="text" onClick={() => setDialogOpen(true)}>
                                    <FormattedMessage id="reviermanager.button.reconfigureCamera" defaultMessage="Kamera neu initialisieren" />
                                </Button>

                                <Button
                                    color="primary"
                                    variant="contained"
                                    endIcon={<ArrowRight />}
                                    disabled={pristine || hasValidationErrors || submitting}
                                    onClick={form.submit}
                                >
                                    <FormattedMessage id="reviermanager.button.sendSMSCode" defaultMessage="Befehl senden" />
                                </Button>
                            </FormButtonsWrapper>
                        </>
                    )}
                />
            </ContentWrapper>
        </>
    );
};

export default SMSCodeForm;
